import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography, Box } from '@mui/material';
import { GroupStore } from '@app/api/GroupStore';
import { GroupView } from '@app/views';
import {
  TeamCelebrationResponseType,
  useAdminCohortReportCardViewCreator,
} from '@app/view-creator/AdminCohortReportCardViewCreator';

interface ReportCardDialogProps {
  open: boolean;
  group: GroupView;
  onClose: () => void;
  onSaved: () => void;
}

export const ReportCardDialog: React.FC<ReportCardDialogProps> = ({ open, group, onClose, onSaved }) => {
  const groupStore = new GroupStore();
  const { data: reportCard, isLoading } = useAdminCohortReportCardViewCreator(group.id, open);

  const handleFinalize = async () => {
    if (window.confirm('Are you sure you want to finalize this cohort? This action cannot be undone.')) {
      await groupStore.FinalizeCohort(group.id);
      onSaved();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: '50px',
          width: 'calc(100vw - 100px)',
          height: 'calc(100vh - 100px)',
          maxWidth: 'none',
          maxHeight: 'none',
        },
      }}
    >
      <DialogTitle>Report Card</DialogTitle>
      <DialogContent style={{ paddingTop: 4 }}>
        {reportCard ? (
          <>
            <Typography variant="h6">Current Pot: ${reportCard.currentPot}</Typography>

            <Typography variant="h6" sx={{ mt: 2 }}>
              Winners:
            </Typography>
            {reportCard.winners.map((winner, i) => (
              <Typography key={i}>
                {winner.participant.user.name} - {winner.successfulWeeks} weeks
              </Typography>
            ))}

            <Typography variant="h6" sx={{ mt: 2 }}>
              Team Celebration:
            </Typography>
            <Typography>{reportCard.teamCelebrationResult}</Typography>

            <Typography variant="h6" sx={{ mt: 2 }}>
              Payouts & Activity:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, alignItems: 'flex-start' }}>
              {reportCard.payouts.map((payout, i) => {
                const participantWeeks = reportCard.participantWeekResults.find(
                  (p) => p.cohortUserId === payout.cohortUserId
                );
                const successfulWeeks = participantWeeks?.weeks.filter((w) => w.success).map((w) => w.week) || [];
                const failedWeeks = participantWeeks?.weeks.filter((w) => !w.success).map((w) => w.week) || [];
                const jailCardWeeks = participantWeeks?.weeks.filter((w) => w.jailCardUsed).map((w) => w.week) || [];
                const failedWithoutJailCard =
                  participantWeeks?.weeks.filter((w) => !w.success && !w.jailCardUsed).map((w) => w.week) || [];

                return (
                  <Box
                    key={i}
                    sx={{
                      width: 280,
                      height: 220,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#f9f9f9',
                      padding: 2,
                      overflow: 'auto',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {payout.userName}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      Payouts:
                    </Typography>
                    <Box sx={{ fontSize: '0.75rem', mb: 1, ml: 1 }}>
                      <div>Remaining Pledge: ${payout.remainingPledge}</div>
                      {reportCard.teamCelebrationResult === TeamCelebrationResponseType.GiftCard && (
                        <div>Gift Card: ${payout.giftCardShare}</div>
                      )}
                      {reportCard.teamCelebrationResult === TeamCelebrationResponseType.Donation && (
                        <div>Donation: ${payout.donationShare}</div>
                      )}
                      {payout.winnerShare > 0 && <div>Winner: ${payout.winnerShare}</div>}
                      <div>
                        <strong>Total: ${payout.totalPayout}</strong>
                      </div>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      Activity:
                    </Typography>
                    <Box sx={{ fontSize: '0.75rem', mb: 1, ml: 1 }}>
                      <div>
                        {successfulWeeks.length} Successful Weeks ({successfulWeeks.join(', ')})
                      </div>
                      <div>
                        {failedWeeks.length} Failed Weeks ({failedWeeks.join(', ')})
                      </div>
                      <div>
                        {jailCardWeeks.length} Jail Cards Used ({jailCardWeeks.join(', ')})
                      </div>
                      <div>
                        <strong>
                          {failedWithoutJailCard.length} Failed w/o Jail Card ({failedWithoutJailCard.join(', ')})
                        </strong>
                      </div>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Typography>No data available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleFinalize} disabled={group.finalized}>
          {group.finalized ? 'Cohort Finalized' : 'Finalize Cohort'}
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
